import { esc, toast } from "./utils.js";
import { navigate } from "./app.js";
import { api, ApiError, uploadPhoto, ALLOWED_IMAGE_TYPES } from "./api.js";
import { confirmAction } from "./confirmDialog.js";
import { infiniteScroll } from "./infiniteScroll.js";

// Mirrors MAX_PHOTOS_PER_ITEM in app/photo_keys.py — the server rejects
// a larger batch, so stop before asking the user to wait for uploads
// that will be refused.
const MAX_PER_BATCH = 10;

/**
 * A grid of photos tagged with a cohort, dive site or dive event.
 *
 * Every tile opens the photo's own page, which is where the full image,
 * its uploader and its tags live. That page exists for every photo, so
 * unlike linking straight to a post there is no such thing as a tile
 * with nowhere to go — a photo whose post was deleted still has one.
 */
export function photoGridHtml(photos, emptyMessage, { canDelete = () => false } = {}) {
  const list = photos || [];
  if (!list.length) return `<p class="text-slate mb-0">${esc(emptyMessage)}</p>`;

  const tiles = list
    .map((photo) => {
      const img = `<img src="${esc(photo.image_url)}" alt="" loading="lazy"
           class="rounded border w-100 h-100" style="object-fit:cover;" />`;
      const body = `<button type="button" class="p-0 border-0 bg-transparent w-100 h-100 gallery-photo"
                   data-photo-id="${photo.id}"
                   aria-label="Open this photo">${img}</button>`;
      // Same corner control as the photo previews on the post form.
      const remove = canDelete(photo)
        ? `<button type="button"
                   class="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 lh-1 rounded-circle gallery-photo-remove"
                   style="width:20px;height:20px;" data-photo-id="${photo.id}"
                   aria-label="Remove this photo">&times;</button>`
        : "";
      return `<div class="position-relative" style="aspect-ratio:1;" data-photo-tile="${photo.id}">${body}${remove}</div>`;
    })
    .join("");

  // auto-fill rather than a fixed column count, so the grid reflows on a
  // phone without a media query.
  return `<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px;">${tiles}</div>`;
}

export function attachPhotoGridHandlers(container, { onDeleted } = {}) {
  container.querySelectorAll(".gallery-photo").forEach((tile) => {
    tile.addEventListener("click", () => navigate(`/photo/${tile.dataset.photoId}`));
  });

  container.querySelectorAll(".gallery-photo-remove").forEach((button) => {
    button.addEventListener("click", async () => {
      // No stopPropagation needed: the remove control is a sibling of
      // the tile's own button, not inside it, so this click never
      // reaches the handler that opens the post. The test that asserts
      // it does not navigate is what keeps that true.
      const confirmed = await confirmAction({
        title: "Remove this photo?",
        body: "It will disappear from every gallery it appears in. The post it was attached to, if any, stays.",
        confirmLabel: "Remove",
      });
      if (!confirmed) return;
      try {
        await api.delete(`/photos/${button.dataset.photoId}`);
        if (onDeleted) await onDeleted();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Could not remove that photo.", "danger");
      }
    });
  });
}


/** The "+ Photo" action, matching the "+ Post" and "+ Plan" buttons.
 *
 * The file input is hidden and driven by the button so the control looks
 * like its siblings rather than like a form field. */
export function photoUploadHtml(id = "photo-add-btn") {
  return `
    <button type="button" class="btn btn-sm btn-outline-primary" id="${esc(id)}">
      <i class="bi bi-plus-lg me-1"></i>Photo
    </button>
    <input type="file" class="d-none" id="${esc(id)}-input"
           accept="${ALLOWED_IMAGE_TYPES.join(",")}" multiple />`;
}

/**
 * Uploads to object storage, then tells the API to tag the results.
 *
 * `endpoint` is the gallery collection ("/cohorts/3/photos"); the server
 * stamps the context, including a dive event's site and cohort, so the
 * client never has to know which columns apply.
 *
 * Uploads run one at a time rather than in parallel: it keeps the
 * progress count honest and avoids opening ten connections to the bucket
 * from a phone. Nothing is written to the API until every key is in
 * hand, so a failed upload leaves no half-populated gallery.
 */
export function attachPhotoUpload(host, { endpoint, onUploaded, id = "photo-add-btn" }) {
  const button = host.querySelector(`#${id}`);
  const input = host.querySelector(`#${id}-input`);
  if (!button || !input) return;

  button.addEventListener("click", () => input.click());

  input.addEventListener("change", async () => {
    const chosen = Array.from(input.files || []);
    // Cleared so re-picking the same file still fires `change`.
    input.value = "";
    if (!chosen.length) return;

    const files = chosen.slice(0, MAX_PER_BATCH);
    if (chosen.length > files.length) {
      toast(`Only the first ${MAX_PER_BATCH} photos were uploaded.`, "warning");
    }

    const label = button.innerHTML;
    button.disabled = true;
    try {
      const photoKeys = [];
      for (const [index, file] of files.entries()) {
        button.textContent = `Uploading ${index + 1} of ${files.length}...`;
        photoKeys.push(await uploadPhoto(file));
      }
      await api.post(endpoint, { photo_keys: photoKeys });
      await onUploaded();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not upload that photo.", "danger");
    } finally {
      button.disabled = false;
      button.innerHTML = label;
    }
  });
}


/**
 * A gallery that pages in as the reader scrolls.
 *
 * Wraps photoGridHtml so a tab panel can hand over a container and a
 * gallery path and get the whole behaviour — first page, more pages,
 * empty state, and the remove control.
 *
 * Returns the scroller so the caller can stop it when the tab changes.
 */
export function infiniteGallery(container, {
  path,
  emptyMessage,
  canDelete = () => false,
  onDeleted,
}) {
  return infiniteScroll(container, {
    path,
    emptyMessage,
    render: (list, photos) => {
      // One grid per page would give a hard break between batches, so
      // the tiles go into a single grid that keeps growing.
      let grid = list.querySelector("[data-role=photo-grid]");
      if (!grid) {
        list.innerHTML = photoGridHtml(photos, emptyMessage, { canDelete });
        grid = list.firstElementChild;
        if (grid) grid.dataset.role = "photo-grid";
      } else {
        const batch = document.createElement("div");
        batch.innerHTML = photoGridHtml(photos, emptyMessage, { canDelete });
        grid.append(...batch.firstElementChild.children);
      }
      attachPhotoGridHandlers(list, { onDeleted });
    },
  });
}
